import { DeviceAIOption, DeviceAIStatus } from '@raydiant/api-client-js';
import Ajv from 'ajv';

import { selectUserHasRole } from '../../../selectors/user';
import { useSelector } from 'react-redux';

import { useSnackbar } from 'notistack';

import Heading from 'raydiant-elements/core/Heading';
import Column from 'raydiant-elements/layout/Column';

import useDeviceAISettings from '../../../hooks/useDeviceAISettings';

import useStyles from './DeviceAISettings.styles';

import React, { useEffect, useMemo, useRef, useState } from 'react';

import apiClient from '../../../clients/miraClient';
import * as D from '../../../clients/mira/types/Device';
import CircularProgress from 'raydiant-elements/core/CircularProgress';
import PaperModal from 'raydiant-elements/core/PaperModal';
import Center from 'raydiant-elements/layout/Center';
import { getDeviceType } from '../../../utilities';
import Text from 'raydiant-elements/core/Text';
import Scrollable from 'raydiant-elements/layout/Scrollable';
import LoadingButton from '../../../components/LoadingButton';
import { LoadingStatus } from '../../../components/LoadingButton/LoadingButton';
import Paper from 'raydiant-elements/core/Paper';
import DeviceAiSettingsVideoModal from './DeviceAiSettingsVideoModal';
import DeviceAiSettingsVideo, {
  DeviceAiSettingsVideoPlayerState,
} from './DeviceAiSettingsVideo';
import DeviceAiSettingsVideoFullScreenPlaceholder from './DeviceAiSettingsVideoFullScreenPlaceholder';

import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import TextField from 'raydiant-elements/core/TextField';
import SelectField from 'raydiant-elements/core/SelectField';
import Switch from 'raydiant-elements/core/Switch';

interface DeviceAISettingsProps {
  device: D.Device;
  visibility?: string;
}

const VIDEO_PLAYBACK_DELAY = 600000; //10 minutes

const DeviceAISettings = ({ device }: DeviceAISettingsProps) => {
  const classes = useStyles();

  const userHasRole = useSelector(selectUserHasRole);

  const [aiSettings, setAiSettings] = useState<D.JSONObject | undefined>(
    undefined,
  );

  const [aiStatus, setAiStatus] = useState<DeviceAIStatus | undefined>(
    undefined,
  );
  const [submitStatus, setSubmitStatus] = useState<LoadingStatus>('idle');
  const deviceId = device.resinUuid;
  const cameraDisconnectedError = aiStatus?.VideoSource_Status === 'OFF';
  const showApplyButton = aiSettings ? true : false;

  const videoPlayer = useRef<any>(null);
  const videoPlayerElement = useRef<HTMLVideoElement>(null);
  const videoPlayerElementModal = useRef<HTMLVideoElement>(null);

  const IVSPlayer = (window as any).IVSPlayer;
  let videoPlaybackTimeout: NodeJS.Timeout;
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [videoStreamURL, setVideoStreamURL] = useState<string | undefined>(
    undefined,
  );
  const [videoPlayerState, setVideoPlayerState] =
    useState<DeviceAiSettingsVideoPlayerState>('idle');

  const [legacySdkSettingsObjects, setLegacySdkSettingsObjects] = useState<
    string[]
  >([]);

  const [aiSettingsFromDb, setAiSettingsFromDb] =
    useState<D.DeviceAIResponse>();

  useEffect(() => {
    if (cameraDisconnectedError) setVideoPlayerState('error');
  }, [cameraDisconnectedError]);

  const { data, isLoading, isError } = useDeviceAISettings(deviceId);

  useEffect(() => {
    if (data) {
      setAiSettingsFromDb(data);
    }
  }, [data]);

  const { enqueueSnackbar } = useSnackbar();
  const getVideoErrors = () => {
    const errors = [];

    if (cameraDisconnectedError) errors.push('Camera disconnected');

    return errors;
  };

  const isHidden = (option: DeviceAIOption) => {
    if (option?.visibility === 'CS' && userHasRole('ai_device_Admin')) {
      return false;
    }

    if (option?.visibility !== 'Client') {
      return !userHasRole('ai_device_' + option.visibility);
    }

    return true;
  };

  const aiSettingsSchemaProperties = useMemo(() => {
    if (!aiSettingsFromDb) return;

    const flattenedAISettings = aiSettingsFromDb.settings;
    const schemaProperties = aiSettingsFromDb.schema.properties;

    if (flattenedAISettings.sdkSettings) {
      const keys = Object.keys(flattenedAISettings.sdkSettings);
      setLegacySdkSettingsObjects(keys);
      Object.assign(flattenedAISettings, flattenedAISettings.sdkSettings);
      delete flattenedAISettings.sdkSettings;
    } else {
      setLegacySdkSettingsObjects([]);
    }

    setAiSettings(flattenedAISettings);
    setAiStatus(aiSettingsFromDb.status);

    return schemaProperties;
  }, [aiSettingsFromDb]);

  const handleUpdateAISettings = async () => {
    if (!aiSettings || !aiSettingsSchemaProperties) return;

    try {
      const newAISettings = { ...aiSettings };

      for (const [key, value] of Object.entries(aiSettings)) {
        if (!aiSettingsSchemaProperties[key]) {
          continue;
        }
        const property = aiSettingsSchemaProperties[key];
        const type = property.type;
        let aiSetting: D.JSONValue;

        switch (type) {
          case 'boolean':
            aiSetting = value;
            break;
          case 'string':
            aiSetting = value;
            break;
          case 'number':
            if (property.number_format === 'float') {
              aiSetting = parseFloat(value as string);
            } else {
              aiSetting = parseInt(value as string, 10);
            }
            break;
          default:
            aiSetting = value;
        }

        newAISettings[key] = aiSetting;
      }

      setSubmitStatus('loading');

      const ajv = new Ajv();
      const validate = ajv.compile({
        type: 'object',
        properties: aiSettingsSchemaProperties,
      });
      const valid = validate(newAISettings);

      if (!valid && validate.errors) {
        const { dataPath, message } = validate.errors[0];
        const errorMessage = `${dataPath} ${message}`;
        enqueueSnackbar(errorMessage, { variant: 'error' });
        throw new Error(errorMessage);
      }

      // Backwards compatilibity for old SDK settings
      if (legacySdkSettingsObjects.length > 0) {
        newAISettings.sdkSettings = {};
        legacySdkSettingsObjects.forEach((key) => {
          (newAISettings.sdkSettings as Record<string, any>)[key] = (
            newAISettings as Record<string, any>
          )[key];
          delete newAISettings[key];
        });
      }

      const response = await apiClient.setDeviceAISettings(
        deviceId,
        newAISettings,
      );
      setAiSettings(response.settings);
      setAiSettingsFromDb((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          settings: response.settings,
        };
      });

      setSubmitStatus('success');
    } catch (error) {
      console.log('ERROR UPDATING AI SETTINGS', error);

      enqueueSnackbar('There was an error updating settings', {
        variant: 'error',
      });

      setSubmitStatus('error');
    }
  };

  const handleStartVideoFeed = async () => {
    try {
      setVideoPlayerState('loading');
      const resp = await apiClient.startDeviceAIVideo(deviceId);

      setVideoStreamURL(resp.playback_url);
      const player = IVSPlayer.create();
      videoPlayer.current = player;
      player.attachHTMLVideoElement(
        isVideoModalOpen
          ? videoPlayerElementModal.current
          : videoPlayerElement.current,
      );
      player.load(resp.playback_url);
      player.play();

      setVideoPlayerState('playing');
      videoPlaybackTimeout = setTimeout(async () => {
        await handleStopVideoFeed();
      }, VIDEO_PLAYBACK_DELAY);
    } catch (error) {
      console.log('ERROR STARTING VIDEO FEED', error);
      enqueueSnackbar('There was an error starting the video feed', {
        variant: 'error',
      });
      setVideoPlayerState('idle');
    }
  };

  const handleStopVideoFeed = async () => {
    try {
      setVideoPlayerState('stopping');
      await apiClient.stopDeviceAIVideo(deviceId);
      setVideoPlayerState('idle');
      videoPlayer.current.delete();
      clearTimeout(videoPlaybackTimeout);
    } catch (error) {
      console.log('ERROR STARTING VIDEO FEED', error);
      enqueueSnackbar('There was an error stopping the video feed', {
        variant: 'error',
      });
      setVideoPlayerState('idle');
      clearTimeout(videoPlaybackTimeout);
    }
  };

  const renderVideoFeedSettings = () => {
    return (
      <div className={classes.section}>
        <Heading size={5} overline gutterBottom>
          Video feed
        </Heading>
        {
          <div style={{ display: isVideoModalOpen ? 'block' : 'none' }}>
            <DeviceAiSettingsVideoFullScreenPlaceholder />
          </div>
        }
        {
          <Paper
            className={classes.video}
            elevation={3}
            style={{ display: isVideoModalOpen ? 'none' : 'block' }}
          >
            <DeviceAiSettingsVideo
              errorMessages={getVideoErrors()}
              hasError={cameraDisconnectedError}
              onPlay={handleStartVideoFeed}
              onOpen={() => handleVideoModalToggle(true)}
              onStop={handleStopVideoFeed}
              showMaximize={true}
              url={videoStreamURL}
              videoPlayerElement={videoPlayerElement}
              videoPlayerState={videoPlayerState}
            />
          </Paper>
        }
      </div>
    );
  };

  const handleChange = (key: string, value: any) => {
    setAiSettings((aiSettings) => ({
      ...aiSettings,
      [key]: value,
    }));
  };

  const renderField = (property: DeviceAIOption) => {
    if (!aiSettings) return null;

    const name = property.$id.replace('#root/', '');
    const value = aiSettings[name];

    let element: JSX.Element | null = null;

    switch (property.type) {
      case 'boolean':
        element = (
          <li className={classes.aiSetting} key={name}>
            <Switch
              checked={!!value}
              onChange={(e) => handleChange(name, e)}
              label={property.title}
            />
          </li>
        );
        break;
      case 'string':
      case 'number':
        if (property.enum) {
          element = (
            <li className={classes.aiSetting} key={name}>
              <SelectField
                label={property.title}
                value={value as string}
                onChange={(e) => handleChange(name, e)}
              >
                {property.enum.map((option: any) => (
                  <option key={option} value={option}>
                    {option.replaceAll('_', ' ')}
                  </option>
                ))}
              </SelectField>
            </li>
          );
        } else {
          element = (
            <li className={classes.aiSetting} key={name}>
              <TextField
                label={property.title}
                type="text"
                value={value as string}
                onChange={(e) => handleChange(name, e)}
              />
            </li>
          );
        }
        break;

      default:
        element = null;
    }

    return element;
  };

  const isApplyButtonEnabled = useMemo(() => {
    if (!aiSettings || !aiSettingsFromDb) return false;

    const isSame =
      JSON.stringify(aiSettings) === JSON.stringify(aiSettingsFromDb.settings);

    return !isSame;
  }, [aiSettings, aiSettingsFromDb]);

  const renderSettings = () => {
    if (isLoading) {
      return (
        <Center>
          <CircularProgress size={30} />
        </Center>
      );
    }

    if (isError) {
      return (
        <Center>
          <>
            <ErrorOutlineIcon className="icon" color="error" />
            <Text small error>
              Error
            </Text>
          </>
        </Center>
      );
    }

    if (aiSettingsFromDb && aiSettingsSchemaProperties) {
      return (
        <>
          {aiSettings && renderVideoFeedSettings()}
          <div className={classes.section}>
            <Heading size={5} overline gutterBottom>
              Settings
            </Heading>
            <ul className={classes.aiSettingList}>
              {Object.entries(aiSettingsSchemaProperties).map(
                ([key, property]) => {
                  if (aiSettings?.hasOwnProperty(key)) {
                    if (!isHidden(property)) {
                      return renderField(property);
                    }
                  }
                  return null;
                },
              )}
            </ul>
          </div>
          {showApplyButton && (
            <div className={classes.section}>
              <LoadingButton
                color={'primary'}
                label={'Apply Changes'}
                disabled={!isApplyButtonEnabled}
                iconAlignment="start"
                fullWidth
                status={submitStatus}
                successLabel="Saved"
                loadingLabel="Saving..."
                errorLabel="Failed!"
                delay={1000}
                onClick={handleUpdateAISettings}
              />
            </div>
          )}
        </>
      );
    }
  };

  const deviceType = getDeviceType(device);

  const handleVideoModalToggle = (isOpen: boolean) => {
    if (isOpen === isVideoModalOpen || !videoPlayer.current) return;

    const player = videoPlayer.current;

    if (videoPlayerState === 'playing') {
      const targetElement = isOpen
        ? videoPlayerElementModal.current
        : videoPlayerElement.current;
      player.attachHTMLVideoElement(targetElement);
      player.load(videoStreamURL);
      player.play();
    }

    setIsVideoModalOpen(isOpen);
  };

  return (
    <>
      <PaperModal.Body>
        <Column>
          <div>
            <Heading className={classes.title}>
              <Text editable value={device.name} />
            </Heading>
            <Heading size={5}>VI Settings</Heading>
            {deviceType && (
              <Heading size={5} className={classes.type}>
                {deviceType}
              </Heading>
            )}
          </div>
        </Column>
        <br />
      </PaperModal.Body>
      <Scrollable>
        <PaperModal.Body>
          <Column>{renderSettings()}</Column>
        </PaperModal.Body>
      </Scrollable>
      <DeviceAiSettingsVideoModal
        isOpen={isVideoModalOpen}
        onClose={() => handleVideoModalToggle(false)}
        onPlay={handleStartVideoFeed}
        onStop={handleStopVideoFeed}
        onChangeRegionOfInterestX={(value) =>
          setAiSettings((prevSettings) => ({
            ...prevSettings,
            ROI_X: value ? parseInt(value, 10) : 0,
          }))
        }
        onChangeRegionOfInterestY={(value) =>
          setAiSettings((prevSettings) => ({
            ...prevSettings,
            ROI_Y: value ? parseInt(value, 10) : 0,
          }))
        }
        onChangeRegionOfInterestW={(value) =>
          setAiSettings((prevSettings) => ({
            ...prevSettings,
            ROI_W: value ? parseInt(value, 10) : 0,
          }))
        }
        onChangeRegionOfInterestH={(value) =>
          setAiSettings((prevSettings) => ({
            ...prevSettings,
            ROI_H: value ? parseInt(value, 10) : 0,
          }))
        }
        regionOfInterestData={{
          x: typeof aiSettings?.ROI_X === 'number' ? aiSettings.ROI_X : 0,
          y: typeof aiSettings?.ROI_Y === 'number' ? aiSettings.ROI_Y : 0,
          w: typeof aiSettings?.ROI_W === 'number' ? aiSettings.ROI_W : 0,
          h: typeof aiSettings?.ROI_H === 'number' ? aiSettings.ROI_H : 0,
        }}
        url={videoStreamURL}
        videoHasError={cameraDisconnectedError}
        videoErrors={getVideoErrors()}
        videoPlayerElement={videoPlayerElementModal}
        videoPlayerState={videoPlayerState}
      />
    </>
  );
};

export default DeviceAISettings;
